import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Plus, LogOut, FileText, Search, BarChart, TrendingUp, Clock, Settings, Image } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [newCase, setNewCase] = useState({ title: '', description: '', case_type: 'investigation', priority: 'medium' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCases();
    fetchStats();
  }, []);

  const fetchCases = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cases`, { withCredentials: true });
      setCases(data);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cases/search/query?${params.toString()}`,
        { withCredentials: true }
      );
      setCases(data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterPriority('');
    fetchCases();
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cases`, newCase, { withCredentials: true });
      toast.success('Case created successfully');
      setShowModal(false);
      setNewCase({ title: '', description: '', case_type: 'investigation', priority: 'medium' });
      fetchCases();
      fetchStats();
    } catch (error) {
      toast.error('Failed to create case');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">{user?.subscription_tier}</span>
            <Link to="/media" className="text-gray-600 hover:text-purple-600 flex items-center text-sm" data-testid="dashboard-media-link">
              <Image className="w-4 h-4 mr-1" /> Media
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-gray-600 hover:text-blue-600 flex items-center text-sm" data-testid="dashboard-admin-link">
                <Settings className="w-4 h-4 mr-1" /> Admin
              </Link>
            )}
            <button onClick={handleLogout} className="text-gray-600 hover:text-gray-900"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard icon={<FileText className="w-8 h-8 text-blue-600" />} title="Total Cases" value={stats.total_cases} />
            <StatCard icon={<TrendingUp className="w-8 h-8 text-green-600" />} title="Open Cases" value={stats.open_cases} />
            <StatCard icon={<BarChart className="w-8 h-8 text-purple-600" />} title="Evidence Files" value={stats.total_evidence} />
            <StatCard icon={<Clock className="w-8 h-8 text-orange-600" />} title="Closed Cases" value={stats.closed_cases} />
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex items-center space-x-2">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Search</button>
            <button onClick={clearFilters} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Clear</button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Cases</h1>
          <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Case</span>
          </button>
        </div>

        {cases.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cases found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search or create a new case</p>
            <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">Create Case</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((c) => (
              <Link key={c.id} to={`/cases/${c.id}`} className="bg-white p-6 rounded-lg shadow hover:shadow-md transition">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-gray-900">{c.title}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${c.priority === 'high' ? 'bg-red-100 text-red-800' : c.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                    {c.priority}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{c.description}</p>
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>{c.case_type}</span>
                  <span className={`px-2 py-1 rounded ${c.status === 'open' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{c.status}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Create New Case</h2>
            <form onSubmit={handleCreateCase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" required value={newCase.title} onChange={(e) => setNewCase({...newCase, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea required value={newCase.description} onChange={(e) => setNewCase({...newCase, description: e.target.value})} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={newCase.case_type} onChange={(e) => setNewCase({...newCase, case_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="investigation">Investigation</option>
                  <option value="research">Research</option>
                  <option value="fraud">Fraud</option>
                  <option value="cybersecurity">Cybersecurity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select value={newCase.priority} onChange={(e) => setNewCase({...newCase, priority: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">Create</button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, title, value }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
      </div>
      <div>{icon}</div>
    </div>
  </div>
);

export default DashboardPage;
